/* 曹洪（字子廉）
 * v0.5.0 重製：照史實做成「護主」的前排。
 * 史實：兗州之亂奪東平、范的糧食供應大軍；滎陽敗於徐榮，曹操失馬，曹洪讓馬「天下可無洪，不可無君」，步行護送到汴水找船渡河；
 * 家富而吝嗇；下辨之戰任都護，與曹休破吳蘭。 */
import { SG, gd, dashTo, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, S } from '../define.ts';

export default defineHero({
  id: 'caohong', name: '曹洪', sub: '字子廉', faction: 'wei', role: '戰士', weapon: 'sword',
  look: { beard: 'mustache', bulk: 1.1, fx: '#ffd46b', c: { plume: '#d9b45c' } },
  skills: [
    ['截糧', 'charge', '朝方向突進斬擊，造成 {70/110/150/190} 傷害。每打中一名敵將回復 {30/45/60/75} 生命，並疊一層「軍糧」：物防 +3，8 秒，最多 3 層。', { sig: 'jieliang', heal: [30, 45, 60, 75], cd: [10, 9, 8, 7], el: null }],
    ['獻馬', 'dash', '衝到一名友方武將身邊，把坐騎讓給他：移速 +40% 2 秒，並得到 {80/130/180/230} 護盾 3 秒。不選人（或選自己）時只有自己得到護盾。', { x: { k: 'xianma', shield: [80, 130, 180, 230], ms: 0.4, speed: 1300, at: 0.05, allyFirst: true }, aim: U(600), cd: [16, 14, 12, 10], mp: 60, el: null }],
    ['鏖戰滎陽', 'roar', '大喝一聲，嘲諷 300 內的敵人 {1/1.2/1.4/1.6} 秒，自己物防 +{4/6/8/10} 3 秒。', { x: { k: 'aozhan', taunt: [1, 1.2, 1.4, 1.6], armor: [4, 6, 8, 10], r: 300, at: 0.15 }, aim: S(300), cd: [15, 14, 13, 12], mp: 70, el: null }],
    ['都護', 'shield', '2 秒內無敵（不受任何傷害），期間擋下的傷害 {24/32/40}% 每 0.5 秒反彈給 350 內所有敵人（小兵、武將都算）。', { x: { k: 'duhu', dur: 2, back: [24, 32, 40], r: 350, at: 0.05 }, aim: S(350), cd: [80, 70, 60], mp: [100, 125, 150], el: null, shout: '天下可無洪，不可無君！' }],
  ],
  kinds: {
    // 獻馬：衝到點選的友方武將身邊，讓他加速並拿護盾；沒選到人就只給自己護盾
    xianma(u, a, X) {
      const R = this.skillRange(a.sk, a.lvl);
      const ok = (o: Unit | null | undefined): o is Unit => !!o && o.alive && o !== u && o.team === u.team && o.kind === 'hero' && gd(u.x, u.y, o.x, o.y) <= R + 60;
      const picked = a.tid != null ? this.units.find((o: Unit) => o.id === a.tid) : null;
      const t = ok(picked) ? picked : this.units.filter((o: Unit) => ok(o) && gd(a.pt[0], a.pt[1], o.x, o.y) < 130)
        .sort((p: Unit, q: Unit) => gd(a.pt[0], a.pt[1], p.x, p.y) - gd(a.pt[0], a.pt[1], q.x, q.y))[0]
        || (u.human ? null : this.xAlly(u, a.pt, R, false)); // 電腦：沒點到人就救最殘血的友將
      const amt = X.shield * spMul(u);
      const shield = (h: Unit) => { h.st.shield = Math.max(h.st.shield || 0, amt); h.st.shieldT = Math.max(h.st.shieldT || 0, 3); this.fx({ k: 'heal', x: h.x, y: h.y, col: u.spec.fx }); };
      const fin = () => { if (u.act && u.act.sk === a.sk) this.endAct(u); };
      if (!t) { shield(u); fin(); return; }
      dashTo(this, u, t, X.speed, (o) => {
        shield(o);
        this.addBuff(o, { id: '獻馬', t: 2, ms: X.ms });
        if (u.human || o.human) this.dmgEvents.push({ x: o.x, y: o.y - 90, text: '獻馬！', team: o.team, src: u.id, tgt: o.id });
        fin();
      });
    },
    // 鏖戰滎陽：嘲諷周圍敵人，自己加物防
    aozhan(u, a, X) {
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => { if (!t.structure) this.applyCC(u, t, { taunt: X.taunt }); });
      this.addBuff(u, { id: a.sk.name, t: 3, armor: X.armor });
      this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
    },
    // 都護：2 秒無敵，擋下的傷害每 0.5 秒按比例反彈給周圍敵人
    duhu(u, a, X) {
      u.duhuT = this.t + X.dur; u.duhuAcc = 0;
      const pulse = (last: boolean) => {
        const d = u.duhuAcc * X.back / 100; u.duhuAcc = 0;
        if (u.alive && d > 0) this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => { if (!t.structure) this.damage(u, t, d, { magic: true, spell: true, quiet: true, reflect: true }); });
        this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
        if (!last) return;
        u.duhuT = 0;
      };
      for (let k = 1; k <= 4; k++) this.later(X.dur * k / 4, () => pulse(k === 4));
      this.fx({ k: 'heal', x: u.x, y: u.y, col: u.spec.fx });
    },
  },
  hooks: [
    // 截糧：打中敵將回血、疊軍糧（每層物防 +3，8 秒，最多 3 層）
    hook('damage.after', 40, function (c) {
      const u = c.own, t = c.t, act = c.act;
      if (!act || act.sk.sig !== 'jieliang' || t.kind !== 'hero' || c.a <= 0 || !u.alive) return; // 對方有護盾時 lost 會是 0，照樣算打中
      if (!act.jlHit) act.jlHit = {};
      if (act.jlHit[t.id]) return;
      act.jlHit[t.id] = true;
      this.heal(u, SG.L(act.sk.heal, act.lvl) * spMul(u));
      const has = u.buffs && u.buffs.some((b: any) => b.id === '軍糧' && b.t > 0);
      u.jlN = has ? Math.min(3, (u.jlN || 0) + 1) : 1;
      this.addBuff(u, { id: '軍糧', t: 8, armor: 3 * u.jlN });
    }),
    // 都護：無敵，記下擋掉的傷害（反彈出去的傷害不會再被另一個都護反彈）
    hook('damage.before', 1, function (c) {
      const t = c.t;
      if (!(t.duhuT > this.t)) return false;
      if (!(c.o && c.o.reflect)) t.duhuAcc = (t.duhuAcc || 0) + c.a;
      return true; // 整下擋掉
    }),
    hook('revive', 60, function (u) { u.duhuT = 0; u.jlN = 0; }),
  ],
  ai: {
    // 獻馬：附近有殘血的友將就去救
    xianma: ({ w, u }) => w.units.some((o: Unit) => o.alive && o !== u && o.team === u.team && o.kind === 'hero' && o.hp / o.maxHp < 0.45 && gd(u.x, u.y, o.x, o.y) < 600 && gd(u.x, u.y, o.x, o.y) > 150),
    aozhan: ({ foeHeroes }) => foeHeroes(300) >= 1,
    duhu: ({ foeHeroes, hpR }) => foeHeroes(350) >= 2 || (hpR < 0.5 && foeHeroes(350) >= 1),
  },
});
